// Table column definitions for DealerDashboard
import React from 'react';
import { Button, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { formatName } from '../../util';
import { PRICE_FILTER_OPTIONS, DATE_FILTER_OPTIONS, STAGE_FILTER_OPTIONS } from './constants';

// Create column definitions that accept render functions as props
export const createLeftSwipesColumns = (renderFilterDropdown, renderSortDropdown, handleViewDealDetails, filterOptions = {}) => [
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>VIN</div>
        {renderFilterDropdown('vin', filterOptions.vin || [])}
      </div>
    ),
    dataIndex: 'vin',
    key: 'vin',
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>STOCK NO</div>
        {renderFilterDropdown('stockNo', filterOptions.stockNo || [])}
      </div>
    ),
    dataIndex: 'stockNo',
    key: 'stockNo',
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>VEHICLE</div>
        {renderFilterDropdown('vehicle', filterOptions.vehicle || [])}
      </div>
    ),
    dataIndex: 'vehicle',
    key: 'vehicle',
    render: (text, record) => (
      <div>
        <div>{text}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>{record.subDetail}</div>
      </div>
    ),
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>CONDITION</div>
        {renderFilterDropdown('condition', filterOptions.condition || [])}
      </div>
    ),
    dataIndex: 'condition',
    key: 'condition',
    render: (text, record) => {
      const milesText = record && record.miles ? record.miles.toString() : '0';
      const numericMiles = parseInt(milesText.replace(/[^0-9]/g, ''), 10) || 0;
      const derivedCondition = numericMiles < 500 ? 'New' : 'Used';
      return (
        <div>
          <Tag color={derivedCondition === 'New' ? 'green' : 'blue'}>{derivedCondition}</Tag>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{record.miles}</div>
        </div>
      );
    },
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>COLOR</div>
        {renderFilterDropdown('color', filterOptions.color || [])}
      </div>
    ),
    dataIndex: 'color',
    key: 'color',
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>PRICE</div>
          {renderSortDropdown('price')}
        </div>
        {renderFilterDropdown('price', PRICE_FILTER_OPTIONS)}
      </div>
    ),
    dataIndex: 'price',
    key: 'price',
    render: (text) => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{text}</span>,
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>REJECTED DATE</div>
          {renderSortDropdown('rejectedDate')}
        </div>
        {renderFilterDropdown('rejectedDate', DATE_FILTER_OPTIONS)}
      </div>
    ),
    dataIndex: 'rejectedDate',
    key: 'rejectedDate',
  },
];

export const createRightSwipesColumns = (renderFilterDropdown, renderSortDropdown, handleViewDealDetails, filterOptions = {}) => [
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>SALES REP</div>
        {renderFilterDropdown('salesRep', filterOptions.salesRep || [], false)}
      </div>
    ),
    dataIndex: 'salesRep',
    key: 'salesRep',
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>MEMBER DETAILS</div>
        {renderFilterDropdown('memberDetails', filterOptions.memberDetails || [], false)}
      </div>
    ),
    dataIndex: 'memberDetails',
    key: 'memberDetails',
    render: (text, record) => {
      const isFPProvided = record.currentStage === 'FP Provided';
      const formattedFirstName = formatName(text);
      const formattedLastName = formatName(record.memberLastName);
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: '8px' }} />
          <div>
            <div>{formattedFirstName} {isFPProvided ? formattedLastName : ''}</div>
            {isFPProvided && (
              <div style={{ fontSize: '11px', color: '#999' }}>
                Loan ID: {record.loanId}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>VEHICLE</div>
        {renderFilterDropdown('vehicle', filterOptions.vehicle || [], false)}
      </div>
    ),
    dataIndex: 'vehicle',
    key: 'vehicle',
    render: (text, record) => (
      <div>
        <div>{text}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>{record.trim}</div>
        <div style={{ fontSize: '11px', color: '#999' }}>
          VIN: {record.vin} | Stock: {record.stock}
        </div>
      </div>
    ),
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>CONDITION</div>
        {renderFilterDropdown('condition', filterOptions.condition || [], false)}
      </div>
    ),
    dataIndex: 'condition',
    key: 'condition',
    render: (text, record) => {
      const tagText = text || 'Used';
      const num = parseInt(String(record.miles || '').replace(/[^0-9]/g, ''), 10);
      const milesDisplay = Number.isFinite(num) ? `${num.toLocaleString()} miles` : (record.miles || '');
      return (
        <div>
          <Tag color={tagText === 'New' ? 'green' : 'blue'}>{tagText}</Tag>
          {milesDisplay ? (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{milesDisplay}</div>
          ) : null}
        </div>
      );
    },
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>MSRP</div>
          {renderSortDropdown('msrp', false)}
        </div>
        {renderFilterDropdown('msrp', PRICE_FILTER_OPTIONS, false)}
      </div>
    ),
    dataIndex: 'msrp',
    key: 'msrp',
  },
  {
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontWeight: '600', fontSize: '14px' }}>CURRENT STAGE</div>
        {renderFilterDropdown('currentStage', filterOptions.currentStage && filterOptions.currentStage.length ? filterOptions.currentStage : STAGE_FILTER_OPTIONS, false)}
      </div>
    ),
    dataIndex: 'currentStage',
    key: 'currentStage',
    render: (text, record) => {
      const getStageColor = (stage) => {
        switch (stage) {
          case 'Prospect Waiting': return 'purple';
          case 'OTD$ Provided': return 'blue';
          case 'Lead Waiting': return 'orange';
          case 'TD Scheduled': return 'cyan';
          case 'FP Provided': return 'green';
          default: return 'default';
        }
      };
      
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tag color={getStageColor(text)}>
            {text}
          </Tag>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', textAlign: 'center' }}>
            {record.stageHistory[record.stageHistory.length - 1]?.date}
          </div>
        </div>
      );
    },
  },
  {
    title: 'ACTION',
    key: 'action',
    render: (text, record) => (
      <Button 
        type="link" 
        style={{ color: '#52c41a' }}
        onClick={() => handleViewDealDetails(record)}
      >
        View Details
      </Button>
    ),
  },
];
